import React, { useEffect, useState, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
    Button,
    Card,
    CardHeader,
    CardBody,
    Container,
    Row,
    Col,
    Alert,
    Spinner,
    ListGroup,
    ListGroupItem,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import { SchemaTree } from "./DynamicProjectInstanceBuilder";
import * as dynamicConfigService from "../../services/api/dynamic-config-template-service";
import SchemaBuilderCore from "./SchemaBuilderCore";
import UserContext from "../../UserContext";

const DynamicSchemaTemplateBuilder = () => {
    const { templateId } = useParams();
    const history = useHistory();
    const context = useContext(UserContext);
    const [meta, setMeta] = useState(null);
    const [schema, setSchema] = useState(null);
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [alert, setAlert] = useState(null);
    const [versionModal, setVersionModal] = useState(null); // { version, schema }
    const [versionModalLoading, setVersionModalLoading] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [confirmRestore, setConfirmRestore] = useState(false);

    const openVersionModal = async (v) => {
        if (v.status !== "published") return;
        setVersionModalLoading(true);
        setConfirmRestore(false);
        setVersionModal({ version: v, schema: null });
        const res = await dynamicConfigService.getBaseVersion(templateId, v.version);
        setVersionModalLoading(false);
        setVersionModal({ version: v, schema: res.status ? (res.data?.schema ?? null) : null });
    };

    const handleRestore = async () => {
        const version = versionModal?.version?.version;
        if (!version) return;
        setRestoring(true);
        const res = await dynamicConfigService.restoreDraftFromBaseVersion(templateId, version);
        setRestoring(false);
        setConfirmRestore(false);
        setVersionModal(null);
        if (res.status) {
            setAlert({ type: "success", msg: `Draft replaced with v${version}. Review it, then save and publish.` });
            await loadAll();
        } else {
            setAlert({ type: "danger", msg: res.error?.message || "Restore failed" });
        }
    };

    const loadAll = async () => {
        setLoading(true);
        const [draftRes, verRes] = await Promise.all([
            dynamicConfigService.getDynamicConfigDraft(templateId),
            dynamicConfigService.listBaseVersions(templateId),
        ]);
        if (draftRes.status && draftRes.data?.meta) {
            setMeta(draftRes.data.meta);
            const d = draftRes.data.draft;
            if (d?.schema) {
                setSchema(d.schema);
                setSelectedNodeId(d.schema.root?.id || null);
            }
        } else if (!draftRes.status && draftRes.error?.errorCode === 403) {
            context.setIsAuthorized(context);
        }
        if (verRes.status && verRes.data) setVersions(verRes.data);
        setLoading(false);
    };

    useEffect(() => {
        loadAll();
    }, [templateId]);

    // Walk every node in the schema tree and collect validation errors
    const validateSchema = (node, errors = [], isRoot = false) => {
        if (!node) return errors;
        if (!isRoot && !(node.key || "").trim()) {
            errors.push(`Node "${node.label || node.id}" has an empty key.`);
        }
        if (node.nodeType === "select" && !node.optionsSource && (!Array.isArray(node.options) || node.options.length === 0)) {
            errors.push(`Select field "${node.key || node.label}" has no options.`);
        }
        if (node.nodeType === "polymorphicArray") {
            if (!(node.discriminatorKey || "").trim()) {
                errors.push(`PolymorphicArray "${node.key || node.label}" is missing a discriminator key.`);
            }
            (node.variants || []).forEach((v, i) => {
                if (String(v.discriminatorValue ?? "").trim() === "") {
                    errors.push(`Variant ${i + 1} of "${node.key || node.label}" has an empty discriminator value.`);
                }
                validateSchema(v.itemSchema, errors, false);
            });
        }
        if (Array.isArray(node.children)) {
            node.children.forEach((ch) => validateSchema(ch, errors, false));
        }
        if (node.itemSchema) {
            validateSchema(node.itemSchema, errors, false);
        }
        return errors;
    };

    const save = async () => {
        if (!schema) return;
        if (!(schema.templateName || "").trim()) {
            setAlert({ type: "danger", msg: "Template name is required before saving." });
            return;
        }
        const errors = validateSchema(schema.root, [], true);
        if (errors.length > 0) {
            setAlert({ type: "danger", msg: `Cannot save — fix these issues first:\n• ${errors.join("\n• ")}` });
            return;
        }
        setSaving(true);
        setAlert(null);
        const res = await dynamicConfigService.saveDynamicConfigDraft(templateId, schema);
        setSaving(false);
        if (res.status) {
            setAlert({ type: "success", msg: "Draft saved." });
            if (res.data?.draft?.schema) setSchema(res.data.draft.schema);
        } else {
            setAlert({ type: "danger", msg: res.error?.message || "Save failed" });
        }
    };

    const publish = async () => {
        setSaving(true);
        setAlert(null);
        const res = await dynamicConfigService.publishDynamicConfigTemplate(templateId);
        setSaving(false);
        if (res.status) {
            setAlert({ type: "success", msg: `Published v${res.data.publishedVersion}.` });
            await loadAll();
        } else {
            setAlert({ type: "danger", msg: res.error?.message || "Publish failed" });
        }
    };

    if (loading || !schema) {
        return (
            <Container className="mt-4">
                <Spinner />
            </Container>
        );
    }

    return (
        <Container fluid className="mt-4">
            <Row className="mb-3">
                <Col>
                    <Button outline size="sm" onClick={() => history.push("/template/dynamic-config-templates")}>
                        ← List
                    </Button>
                    <h4 className="d-inline ml-3">Base template: {meta?.name}</h4>
                    <Badge className="ml-2" color="secondary">
                        key: {meta?.templateKey}
                    </Badge>
                    <Badge className="ml-1" color="info">
                        published v{meta?.currentVersion || 0}
                    </Badge>
                </Col>
            </Row>
            {alert && (
                <Alert color={alert.type} style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                    <span>{alert.msg}</span>
                    <button
                        type="button"
                        onClick={() => setAlert(null)}
                        style={{
                            background: "rgba(0,0,0,0.2)",
                            border: "none",
                            borderRadius: 4,
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: "1rem",
                            lineHeight: 1,
                            padding: "2px 7px",
                            marginLeft: 12,
                            cursor: "pointer",
                            flexShrink: 0,
                        }}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </Alert>
            )}
            <Row>
                <Col md="3">
                    <Card>
                        <CardHeader>Base version history</CardHeader>
                        <CardBody style={{ maxHeight: 400, overflow: "auto", padding: "0.5rem" }}>
                            <ListGroup flush>
                                {versions.map((v) => (
                                    <ListGroupItem
                                        key={`${v.version}-${v.status}`}
                                        action={v.status === "published"}
                                        onClick={v.status === "published" ? () => openVersionModal(v) : undefined}
                                        style={{
                                            padding: "8px 10px",
                                            cursor: v.status === "published" ? "pointer" : "default",
                                            borderLeft: v.version === meta?.currentVersion && v.status === "published"
                                                ? "3px solid #28a745"
                                                : "3px solid transparent",
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <span style={{ fontWeight: 600, fontSize: "0.9em" }}>v{v.version}</span>
                                            <Badge
                                                color={v.status === "draft" ? "warning" : "success"}
                                                style={{ fontSize: "0.7em" }}
                                            >
                                                {v.status}
                                            </Badge>
                                        </div>
                                        {v.updatedAt && (
                                            <div style={{ fontSize: "0.72em", color: "#888", marginTop: 2 }}>
                                                {new Date(v.updatedAt).toLocaleString()}
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </CardBody>
                    </Card>
                </Col>
                <Col md="9">
                    <Card className="mb-3">
                        <CardBody>
                            <Button color="primary" className="mr-2" onClick={save} disabled={saving}>
                                Save draft
                            </Button>
                            <Button color="success" onClick={publish} disabled={saving}>
                                Publish
                            </Button>
                        </CardBody>
                    </Card>
                    <SchemaBuilderCore
                        schema={schema}
                        setSchema={setSchema}
                        selectedNodeId={selectedNodeId}
                        setSelectedNodeId={setSelectedNodeId}
                        expanded={expanded}
                        setExpanded={setExpanded}
                    />
                </Col>
            </Row>
            <Modal isOpen={!!versionModal} toggle={() => setVersionModal(null)} size="lg">
                <ModalHeader style={{ position: "relative" }}>
                    <span>
                        Base version v{versionModal?.version?.version}
                        <Badge color="success" className="ml-2" style={{ fontSize: "0.75em", verticalAlign: "middle" }}>
                            published
                        </Badge>
                    </span>
                    <button
                        type="button"
                        className="close"
                        onClick={() => setVersionModal(null)}
                        style={{ position: "absolute", right: "1rem", top: "1rem" }}
                        aria-label="Close"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </ModalHeader>
                <ModalBody>
                    {versionModalLoading ? (
                        <Spinner size="sm" />
                    ) : versionModal?.schema ? (
                        <SchemaTree schema={versionModal.schema} />
                    ) : (
                        <span className="text-muted small">Schema not available.</span>
                    )}
                </ModalBody>
                <ModalFooter>
                    {confirmRestore ? (
                        <div className="w-100">
                            <p className="small text-danger mb-2">
                                This replaces the current draft with v{versionModal?.version?.version}.
                                Any unsaved or unpublished draft changes are lost and cannot be recovered.
                            </p>
                            <Button color="danger" size="sm" disabled={restoring} onClick={handleRestore}>
                                {restoring ? <Spinner size="sm" /> : "Yes, replace draft"}
                            </Button>
                            <Button color="secondary" size="sm" className="ml-2" disabled={restoring} onClick={() => setConfirmRestore(false)}>
                                Cancel
                            </Button>
                        </div>
                    ) : (
                        <Button color="primary" size="sm" disabled={!versionModal?.schema} onClick={() => setConfirmRestore(true)}>
                            Restore into draft
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default DynamicSchemaTemplateBuilder;
